import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMe } from "../../hooks/useMe";
import {
  partitionMineAndClaimable,
  useMyCards,
  useMyMentions,
  useMyOperatorSteps,
  useOperatorStepMutations,
  useRecentlyCompletedSteps,
  useReviewerSteps,
  type CardItem,
  type InboxStepItem,
  type MentionItem,
  type OperatorTab,
} from "../../hooks/useOperator";
import { DailyBriefTab } from "./DailyBriefTab";

const TABS: ReadonlyArray<{ key: OperatorTab; label: string }> = [
  { key: "today", label: "Today" },
  { key: "cards", label: "Cards" },
  { key: "mine", label: "My steps" },
  { key: "claimable", label: "Claimable" },
  { key: "review", label: "Awaiting my review" },
  { key: "completed", label: "Recently completed" },
  { key: "mentions", label: "Mentions" },
];

const STATE_PILL_CLASS: Record<string, string> = {
  ready: "",
  running: "green",
  awaiting_review: "amber",
  paused: "gray",
  completed: "gray",
  failed: "red",
  pending: "gray",
  skipped: "gray",
};

const TYPE_GLYPH: Record<string, string> = {
  decision: "◆",
  comm: "✉",
  query: "?",
  micro_task: "◦",
  wait: "⌛",
  milestone: "◇",
  instrumentation: "⟳",
};

function isInlineCompletable(type: string): boolean {
  return type === "milestone" || type === "comm" || type === "decision";
}

export function HandsOps() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [tab, setTab] = useState<OperatorTab>("today");

  const mineQ = useMyOperatorSteps();
  const reviewQ = useReviewerSteps();
  const completedQ = useRecentlyCompletedSteps();
  const mentionsQ = useMyMentions();
  const cardsQ = useMyCards(workspaceId);
  const { claim, complete, actionCard } = useOperatorStepMutations();

  const partitioned = useMemo(() => {
    const items = mineQ.data?.items ?? [];
    return partitionMineAndClaimable(items, me?.user.id);
  }, [mineQ.data, me?.user.id]);

  const counts: Record<OperatorTab, number> = {
    today: partitioned.mine.length,
    cards: cardsQ.data?.cards.length ?? 0,
    mine: partitioned.mine.length,
    claimable: partitioned.claimable.length,
    review: reviewQ.data?.items.length ?? 0,
    completed: completedQ.data?.items.length ?? 0,
    mentions: mentionsQ.data?.items.length ?? 0,
  };

  const openStep = (it: InboxStepItem) => {
    navigate(`/workspaces/${it.play.workspaceId}/tasks/${it.step.id}`);
  };

  const onClaim = (e: React.MouseEvent, stepId: string) => {
    e.stopPropagation();
    claim.mutate(stepId);
  };

  const onComplete = (e: React.MouseEvent, stepId: string) => {
    e.stopPropagation();
    // Inline complete with an empty output payload — heavier types route to PlayWorkspace.
    complete.mutate({ stepId, output: {} });
  };

  const renderRows = (items: InboxStepItem[], variant: OperatorTab) => {
    if (items.length === 0) {
      const message = emptyMessageFor(variant);
      return (
        <div className="ho-empty" data-testid="handsops-empty">
          {message}
        </div>
      );
    }
    return (
      <div className="st-list" data-testid="handsops-rows">
        {items.map((it) => {
          const stepId = it.step.id;
          const playLabel = `${it.play.workspaceName} · ${it.play.archetype}`;
          const stateClass = STATE_PILL_CLASS[it.step.state] ?? "";
          return (
            <div
              key={stepId}
              className="st-row"
              data-testid={`handsops-row-${stepId}`}
              onClick={() => openStep(it)}
            >
              <div className="st-l">
                <span className={`pd-step-type pd-type-${it.step.type}`}>
                  {TYPE_GLYPH[it.step.type] ?? "•"} {it.step.type}
                </span>
                <span className="ord">#{it.step.ord}</span>
              </div>
              <div className="st-b">
                <div className="st-s">{it.step.title}</div>
                <div className="st-p">{playLabel}</div>
              </div>
              <div className="st-r" onClick={(e) => e.stopPropagation()}>
                <span className={`pill ${stateClass}`}>{it.step.state}</span>
                {it.play.targetWindowEnd ? (
                  <span className="st-due">
                    due {formatDue(it.play.targetWindowEnd)}
                  </span>
                ) : null}
                {variant === "claimable" ? (
                  <button
                    type="button"
                    className="btn primary"
                    data-testid={`handsops-claim-${stepId}`}
                    onClick={(e) => onClaim(e, stepId)}
                    disabled={claim.isPending}
                  >
                    Claim
                  </button>
                ) : null}
                {variant === "mine" && isInlineCompletable(it.step.type) ? (
                  <button
                    type="button"
                    className="btn"
                    data-testid={`handsops-complete-${stepId}`}
                    onClick={(e) => onComplete(e, stepId)}
                    disabled={complete.isPending}
                  >
                    Complete
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn"
                  data-testid={`handsops-open-${stepId}`}
                  onClick={() => openStep(it)}
                >
                  Open
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMentions = (items: MentionItem[]) => {
    if (items.length === 0) {
      return (
        <div className="ho-empty" data-testid="handsops-empty">
          No mentions yet.
        </div>
      );
    }
    return (
      <div className="st-list" data-testid="handsops-mentions">
        {items.map((m) => (
          <div
            key={m.comment.id}
            className="st-row"
            data-testid={`handsops-mention-${m.comment.id}`}
            onClick={() =>
              navigate(
                `/workspaces/${m.play.workspaceId}/plays/${m.play.id}${
                  m.step ? `#step-${m.step.id}` : ""
                }`,
              )
            }
          >
            <div className="st-b">
              <div className="st-s">@{m.author.displayName}</div>
              <div className="st-p">
                {m.play.workspaceName} · {m.play.archetype}
                {m.step ? ` · ${m.step.title}` : ""}
              </div>
              <div className="st-mention-body">{m.comment.body}</div>
            </div>
            <div className="st-r">
              <span className="st-due">{formatRelative(m.comment.createdAt)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const CARD_TYPE_LABEL: Record<string, string> = {
    review: "📋 Review",
    decision: "🤔 Decision",
    confirmation: "✅ Confirmation",
    intervention: "⚠️ Intervention",
    milestone: "🏁 Milestone",
  };

  const CARD_ACTIONS: Record<string, string[]> = {
    review: ["approve_all", "refine", "reject"],
    decision: ["select"],
    confirmation: ["confirm", "defer"],
    intervention: ["retry", "skip"],
    milestone: ["acknowledge"],
  };

  const renderCards = (cards: CardItem[]) => {
    if (cards.length === 0) {
      return (
        <div className="ho-empty" data-testid="handsops-empty">
          No open cards. You are all caught up.
        </div>
      );
    }
    return (
      <div className="st-list" data-testid="handsops-cards">
        {cards.map((card) => {
          const actions = CARD_ACTIONS[card.type] ?? [];
          const title =
            (card.body["step_title"] as string | undefined) ??
            (card.body["title"] as string | undefined) ??
            card.type;
          return (
            <div
              key={card.id}
              className="st-row"
              data-testid={`handsops-card-${card.id}`}
              onClick={() =>
                navigate(
                  `/workspaces/${card.workspaceId}/tasks/${card.stepId}`,
                )
              }
            >
              <div className="st-l">
                <span className={`pill ${card.criticality === "high" ? "red" : card.criticality === "medium" ? "amber" : ""}`}>
                  {CARD_TYPE_LABEL[card.type] ?? card.type}
                </span>
              </div>
              <div className="st-b">
                <div className="st-s">{String(title)}</div>
                {card.body["context"] ? (
                  <div className="st-p" style={{ whiteSpace: "pre-wrap" }}>
                    {String(card.body["context"]).slice(0, 120)}
                    {String(card.body["context"]).length > 120 ? "…" : ""}
                  </div>
                ) : null}
              </div>
              <div className="st-r" onClick={(e) => e.stopPropagation()}>
                {actions.slice(0, 2).map((action) => (
                  <button
                    key={action}
                    type="button"
                    className={`btn ${action === "approve_all" || action === "confirm" ? "primary" : ""}`}
                    data-testid={`handsops-card-action-${card.id}-${action}`}
                    disabled={actionCard.isPending}
                    onClick={() =>
                      actionCard.mutate({
                        workspaceId: card.workspaceId,
                        cardId: card.id,
                        action,
                      })
                    }
                  >
                    {action.replace(/_/g, " ")}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    navigate(
                      `/workspaces/${card.workspaceId}/plays/${card.playId}#step-${card.stepId}`,
                    )
                  }
                >
                  Open
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Derive what to show for the active tab, then render once below so
  // loading/error/empty states all flow through one branch.
  let body: JSX.Element;
  if (tab === "today") {
    body = <DailyBriefTab workspaceId={workspaceId ?? null} />;
  } else if (tab === "cards") {
    body = cardsQ.isLoading
      ? loadingSkeleton()
      : renderCards(cardsQ.data?.cards ?? []);
  } else if (tab === "mine") {
    body = mineQ.isLoading
      ? loadingSkeleton()
      : renderRows(partitioned.mine, "mine");
  } else if (tab === "claimable") {
    body = mineQ.isLoading
      ? loadingSkeleton()
      : renderRows(partitioned.claimable, "claimable");
  } else if (tab === "review") {
    body = reviewQ.isLoading
      ? loadingSkeleton()
      : renderRows(reviewQ.data?.items ?? [], "review");
  } else if (tab === "completed") {
    body = completedQ.isLoading
      ? loadingSkeleton()
      : renderRows(completedQ.data?.items ?? [], "completed");
  } else {
    body = mentionsQ.isLoading
      ? loadingSkeleton()
      : renderMentions(mentionsQ.data?.items ?? []);
  }

  return (
    <div className="main-inner" data-testid="handsops" data-workspace={workspaceId}>
      <div className="greet-row">
        <span className="greet">My Tasks</span>
        <span className="greet-date">ACROSS ROUTINES · YOUR WORK</span>
      </div>
      <div className="subgreet">
        <div className="lede">
          {counts.cards > 0 ? <><b>{counts.cards}</b> open cards · </> : null}<b>{counts.mine}</b> on you · <b>{counts.claimable}</b> claimable ·{" "}
          <b>{counts.review}</b> awaiting your review.
        </div>
      </div>

      <div className="ho-tabs" data-testid="handsops-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`handsops-tab-${t.key}`}
            className={`ho-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className="badge">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {body}
    </div>
  );
}

function emptyMessageFor(tab: OperatorTab): string {
  if (tab === "mine") return "Queue is clear. Take a break.";
  if (tab === "claimable") return "Nothing claimable right now.";
  if (tab === "review") return "Nothing awaiting your review.";
  if (tab === "completed") return "No recently completed steps.";
  if (tab === "cards") return "No open cards.";
  return "No mentions yet.";
}

function loadingSkeleton(): JSX.Element {
  return (
    <div data-testid="handsops-loading" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{ height: 56, borderRadius: 8, background: "var(--bg-soft)", opacity: 0.6 }}
        />
      ))}
    </div>
  );
}

function formatDue(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}
